import {
    AuthError,
    BrowserAuthError,
    InteractionRequiredAuthError,
    type IPublicClientApplication,
    type SilentRequest,
} from "@azure/msal-browser";
import { scopesNeeded } from "./authConfig";

/**
 * Access-token acquisition for `fetch.ts`.
 *
 * `fetch.ts` no longer depends on MSAL directly. It asks whatever *token source*
 * is registered for the current surface:
 *
 *   - Main SPA        -> an MSAL-backed source (silent renewal, redirect handled
 *                        by `MsalAuthenticationTemplate`).
 *   - Widget / copilot -> either a parent-supplied-token source (the host page
 *                        mints a token and posts it in via `cts-auth-token`), or
 *                        an MSAL source that NEVER opens a popup from a background
 *                        fetch - interactive auth only ever runs from a real user
 *                        click inside the embed's own auth provider.
 *
 * A cross-origin iframe cannot reliably open `window.open` without a user gesture
 * (returns `null` -> `empty_window_error`) and cannot use MSAL's hidden-iframe
 * silent renewal (third-party-cookie blocking). So the only correct places for a
 * popup are (a) one opportunistic `prompt:"none"` attempt on mount and (b) the
 * "Sign in" button click - both owned by `WidgetAuthProvider` /
 * `CopilotAuthProvider`, not by this module.
 */

export type AcquireTokenOptions = {
    forceRefresh?: boolean;
};

export type TokenSource = (options?: AcquireTokenOptions) => Promise<string | null>;

interface CustomWindow extends Window {
    __DEBUG_FORCE_INTERACTION_REQUIRED?: boolean;
}

export const DEBUG_FORCE_INTERACTION_REQUIRED = (): boolean =>
    import.meta.env.VITE_ENVIRONMENT === "dev" &&
    (window as CustomWindow).__DEBUG_FORCE_INTERACTION_REQUIRED === true;

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

let registeredSource: TokenSource | null = null;

/**
 * Register the process-wide token source `fetch.ts` uses. Last writer wins; the
 * app surface (main / widget / copilot) sets this once its auth state resolves.
 */
export const setTokenSource = (source: TokenSource): void => {
    registeredSource = source;
};

/** Drop the registered source (call on unmount of the surface that set it). */
export const clearTokenSource = (): void => {
    registeredSource = null;
};

export const hasTokenSource = (): boolean => registeredSource !== null;

export const getRegisteredTokenSource = (): TokenSource | null => registeredSource;

/** Event name dispatched on `window` when silent renewal fails and a real
 *  (click-driven) sign-in is required. The widget / copilot auth providers
 *  listen for it to re-run their SSO / Sign In button flow. */
export const AUTH_REQUIRED_EVENT = "cts-auth-required";

/**
 * Acquire an access token from the registered source. Throws if no source is
 * registered - used by non-`postAPI` call sites (e.g. the widget's raw SSE
 * fetch) that still need a bearer token.
 */
export const getAuthToken = (options?: AcquireTokenOptions): Promise<string | null> => {
    if (!registeredSource) {
        return Promise.reject(new Error("No auth token source registered"));
    }
    return registeredSource(options);
};

// ---------------------------------------------------------------------------
// interaction_in_progress recovery helpers
// ---------------------------------------------------------------------------

const INTERACTION_RETRY_DELAY_MS = 500;
const INTERACTION_MAX_RETRIES = 10;

const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

export const isInteractionInProgressError = (error: unknown): boolean =>
    error instanceof BrowserAuthError &&
    (error.errorCode === "interaction_in_progress" ||
        error.message.includes("interaction_in_progress"));

/**
 * Tell the surrounding surface that silent renewal failed and a real
 * (click-driven) sign-in is required. `WidgetAuthProvider` / `CopilotAuthProvider`
 * listen for this and re-run their SSO / Sign In button flow; the host page can
 * listen for the postMessage to supply a fresh token or a login hint.
 */
const signalInteractionRequired = (): void => {
    try {
        window.dispatchEvent(new CustomEvent(AUTH_REQUIRED_EVENT));
        if (window.parent !== window) {
            window.parent.postMessage({ type: AUTH_REQUIRED_EVENT }, "*");
        }
    } catch {
        // dispatch/postMessage can throw in exotic sandboxes - never fatal here.
    }
};

// ---------------------------------------------------------------------------
// MSAL-backed source
// ---------------------------------------------------------------------------

/**
 * Interactive fallback. In an iframe this NEVER opens a popup - a background
 * fetch has no user activation, so the popup would be blocked and yield a null
 * token that 401-loops. It signals the auth provider instead and returns null.
 * Outside an iframe it also returns null: `MsalAuthenticationTemplate` owns the
 * redirect, and racing it here causes `interaction_in_progress`.
 */
const acquireTokenInteractive = async (): Promise<string | null> => {
    signalInteractionRequired();
    return null;
};

const acquireMsalToken = async (
    instance: IPublicClientApplication,
    forceRefresh: boolean,
): Promise<string | null> => {
    const account = instance.getActiveAccount() || instance.getAllAccounts()[0] || null;

    // Use proper scopes to enable MSAL caching - empty scopes bypass the cache
    const trySilent = async (): Promise<string> => {
        if (DEBUG_FORCE_INTERACTION_REQUIRED()) {
            throw new InteractionRequiredAuthError();
        }

        const silentRequest: SilentRequest = {
            scopes: scopesNeeded, // Required scopes for proper caching
            forceRefresh,
            ...(account && { account }), // Include account if available to use cached tokens
        };

        const tokenResponse = await instance.acquireTokenSilent(silentRequest);
        if (!tokenResponse.accessToken) {
            throw new Error("No token returned from MSAL");
        }
        return tokenResponse.accessToken;
    };

    try {
        return await trySilent();
    } catch (error) {
        // Another interactive flow holds MSAL's global interaction lock. Poll for
        // it to clear, retrying the silent acquisition (which then hits the cache).
        if (isInteractionInProgressError(error)) {
            for (let attempt = 0; attempt < INTERACTION_MAX_RETRIES; attempt++) {
                await wait(INTERACTION_RETRY_DELAY_MS);
                try {
                    return await trySilent();
                } catch (retryError) {
                    if (isInteractionInProgressError(retryError)) {
                        continue;
                    }
                    if (
                        retryError instanceof InteractionRequiredAuthError ||
                        retryError instanceof AuthError
                    ) {
                        return acquireTokenInteractive();
                    }
                    throw retryError;
                }
            }
            // Lock never cleared within the retry window - likely stale.
            throw new Error("Authentication in progress, please wait");
        }

        if (error instanceof InteractionRequiredAuthError || error instanceof AuthError) {
            return acquireTokenInteractive();
        }

        throw error;
    }
};

/**
 * A token source backed by an MSAL instance: silent renewal only, with
 * interaction_in_progress recovery. Never opens a popup (see
 * `acquireTokenInteractive`).
 */
export const createMsalTokenSource =
    (instance: IPublicClientApplication): TokenSource =>
    ({ forceRefresh = false } = {}) =>
        acquireMsalToken(instance, forceRefresh);

// ---------------------------------------------------------------------------
// Parent-supplied-token source (widget / copilot embeds)
// ---------------------------------------------------------------------------

export type ParentTokenSourceConfig = {
    /** The token last handed down by the host (via `cts-auth-token`), or null. */
    getCurrentToken: () => string | null;
    /**
     * Ask the host for a fresh token and resolve with it (or null on
     * timeout / refusal). The provider owns the `cts-request-token` /
     * `cts-token-response` round-trip.
     */
    requestFreshToken: () => Promise<string | null>;
};

/**
 * A token source for an embed whose host page supplies the access token. This is
 * the fully-silent path: no popup, no third-party cookies, no MSAL in the
 * iframe. Falls back to `requestFreshToken` when there is no cached token or a
 * forced refresh is asked for.
 */
export const createParentTokenSource =
    (config: ParentTokenSourceConfig): TokenSource =>
    async ({ forceRefresh = false } = {}) => {
        if (!forceRefresh) {
            const current = config.getCurrentToken();
            if (current) {
                return current;
            }
        }
        return config.requestFreshToken();
    };
